import * as React from 'react';
import { createLocators } from '@u1/locators';
import useStyles from './Field.styles';

import { Typography } from '../Typography';

import { ILocatorOverrides } from '../../../types/utility';
import clsx from 'clsx';

export interface IFieldProps extends Omit<ILabelProps, 'locatorsOverrides'> {
  /**
   * Text string at the bottom of the input
   */
  helperText?: React.ReactNode;
  /**
   * Error message. Replaces the helper text if the input is in invalid state
   */
  errorMessage?: React.ReactNode;
  /**
   * Sets minimum height for the lable. Used labels placed below the input to provide space for an error message
   */
  withMinHeight?: boolean;
}

export interface ILabelProps extends ILocatorOverrides {
  /**
   * Label for an input component
   */
  label?: React.ReactNode;
  /**
   * Extra lablel displayed on the right hand side
   */
  additionalLabel?: React.ReactNode;
  /**
   * Is input reuired
   * @default false
   */
  required?: boolean;
  /**
   * Is input invalid
   * @default false
   */
  invalid?: boolean;
  /**
   * Is input disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * Sets minimum height for the lable container. Used in labels placed below the input to provide space for an error message
   */
  withMinHeight?: boolean;
}

export const fieldLocators = createLocators({
  name: 'Field',
  componentLocators: [
    'label',
    'additinalLabel',
    'field',
    'options',
    'input',
    'bottomLabel',
  ],
});
const { locators } = fieldLocators();

export const Label: React.FC<ILabelProps> = ({
  label,
  required,
  disabled,
  invalid,
  withMinHeight,
  locatorsOverrides,
}) => {
  const classes = useStyles();

  return (
    <Typography
      as="div"
      className={clsx({
        [classes.label]: true,
        [classes.required]: required,
        [classes.disabled]: disabled,
        [classes.invalid]: invalid,
        [classes.withMinHeight]: withMinHeight,
      })}
      variant="label01"
      locatorsOverrides={locatorsOverrides}
    >
      {label}
    </Typography>
  );
};

const Field: React.FC<React.PropsWithChildren<IFieldProps>> = ({
  required,
  label,
  additionalLabel,
  invalid,
  disabled,
  helperText,
  errorMessage,
  children,
}) => {
  const classes = useStyles();

  return (
    <div {...locators.field()} className={classes.root}>
      <div className={classes.labelWrap}>
        <Label
          locatorsOverrides={locators.label({ required, disabled })}
          label={label}
          required={required}
          disabled={disabled}
        />
        <Label
          locatorsOverrides={locators.additinalLabel({ required, disabled })}
          label={additionalLabel}
          disabled={disabled}
        />
      </div>

      <div {...locators.input()} className={classes.input}>
        {React.cloneElement(children as React.ReactElement, {
          disabled,
          invalid,
        })}
      </div>

      <Label
        locatorsOverrides={locators.bottomLabel({ invalid })}
        label={invalid ? errorMessage : helperText}
        disabled={disabled}
        invalid={invalid}
        withMinHeight
      />
    </div>
  );
};

export default Field;
